import { GenericTabView } from "./generic-tab";
import { CitationsAndSourcesTabModel } from "./../../models/tabs/citations-and-sources";
import { PublisherModel } from "./../../models/publisher";
import { ParallelCitationsView } from "./../partials/parallel-citations";
import { PublisherLinkListsView } from "./../partials/publisher-link-lists";
import { CitationLinksModel } from "./../../models/citation-links";
import { FullTextLinksRelationsView } from "./../partials/full-text-links-relations";
import { SourceLinkCollection } from "./../../collections/source-links";
import { CitationsAndSourcesControlsView } from "./citations-and-sources-controls";
import { CitationLinkModel } from "../../models/citation-link-model";
import { ParallelCitationModel } from "../../models/parallel-citation";
import { Container } from "aurelia-dependency-injection";
import { AuthService } from "../../../authentication/auth-service";
import { ContainerHelper } from "../../../shared/helpers/container-helper";

const lookupLinkTemplate = require<string>("../../../desktop/views/partials/abbreviation-lookup-link.html");
const dom = {
    FULLTEXT_LINK: ".full-text-link",
    PUBLISHER_LINK: ".publisher-full-text-link"
};

export class CitationsAndSourcesTabView extends GenericTabView {

    public model: CitationsAndSourcesTabModel;
    private byCitationView: Marionette.View<any>;
    private byCitationRegion: Marionette.Region;
    private byPublisherView: Marionette.View<any>;
    private byPublisherRegion: Marionette.Region;
    private controlsView: CitationsAndSourcesControlsView;
    private controlsRegion: Marionette.Region;

    private sourceCollection: SourceLinkCollection;
    private authService: AuthService;

    public constructor(options?: any) {
        super(options);
        this.sourceCollection = new SourceLinkCollection();
        this.authService = ContainerHelper.AuthService;
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click " + dom.FULLTEXT_LINK] = (e) => this.fullTextLinkClick(e, "Citation");
        events["click " + dom.PUBLISHER_LINK] = (e) => this.fullTextLinkClick(e, "Publisher");
        return events;
    }

    public regions(): any {
        return {
            controlsRegion: ".controls-region",
            byCitationRegion: ".sources-by-citation-region",
            byPublisherRegion: ".sources-by-publisher-region"
        };
    }

    public onShow() {
        this.loadAllFulltextLinks();
        this.setupSubViews();
    }

    public setupSubViews() {
        this.setupControlsView();
        this.setupListViews();
    }

    public loadAllFulltextLinks(): void {

        // Show Citations links panel by default on initial load
        this.$(".no-pub-msg").hide();
        this.$(".full-text-sources-by-citation").show();
        this.$(".full-text-sources-by-publisher").hide();

        // get all references
        const references = this.model.Data.ParallelCitations.map((reference) => reference.get("Reference"));
        this.sourceCollection.fetchAll(references).then(() => {
            this.$(".full-text-link-loading, .publisher-full-text-link-loading").hide();
            if (this.model.Data.Publishers.size() === 0) {
                this.populatePublisherList(this.sourceCollection.Sources);
            }
            this.renderLinksByCitation(this.sourceCollection.Sources);
        });
    }

    protected setupListViews() {

        try {

            this.byCitationView = new ParallelCitationsView({model: this.model.Data});
            this.byCitationRegion.show(this.byCitationView);

            this.byPublisherView = new PublisherLinkListsView({collection: this.model.Data.Publishers});
            this.byPublisherRegion.show(this.byPublisherView);

        } catch (e) {
            // region doesn't exist on this sku
        }
    }

    protected setupControlsView() {
        try {

            this.controlsView = new CitationsAndSourcesControlsView(() => this.fullTextSourcesToggleClick(), (e) => this.linkSortClick(e));
            this.controlsRegion.show(this.controlsView);
        } catch (e) {
            // region doesn't exist on this sku
        }

    }

    private populatePublisherList(data: Backbone.Collection<CitationLinksModel>) {
        const publishers = this.model.Data.Publishers;
        const parallelCitations = this.model.Data.ParallelCitations;
        const reportSources = this.getJustisOneReportSources();
        const fullTextSources = this.getJustisOneFullTextSources();

        data.each((linkList) => {
            // Add the links list to the documents publisher list
            const links = linkList.Links;
            const missingLinks = [];
            links.forEach((link) => {
                if (link.IsJustisOneUrl && !this.model.Data.HasFullTextOrPdf && !(this.model.Data.ReportDetails && this.model.Data.ReportDetails.length > 0)) {
                    missingLinks.push(link);
                    return;
                }

                const publisher = link.Publisher;
                if (publisher) {
                    const pubName = publisher.Name;
                    let existingPublisher;
                    existingPublisher = publishers.get(pubName);
                    if (!existingPublisher) {
                        existingPublisher = new PublisherModel({ id: publisher.Name, Publisher: publisher, Citations: [], DisplayLink: link.get("DisplayLink") });
                        publishers.add(existingPublisher);
                    }

                    const detail = parallelCitations.where({ Reference: linkList.Citation })[0];
                    if (detail) {
                        if (detail.Source && detail.Source.Name) {
                            if (detail.Source.Code) {
                                this.determineLinkTypes(detail.Source.Code, reportSources, link, fullTextSources);

                                const isMissingJustisOneLink = link.IsJustisOneUrl && !link.IsJudgmentUrl && !link.IsReportUrl && !link.IsLegislationUrl;
                                if (isMissingJustisOneLink) {
                                    missingLinks.push(link);
                                    return;
                                }
                            }
                        } else {
                            missingLinks.push(link);
                            return;
                        }

                        this.addToExistingPublisher(existingPublisher, linkList.Citation, detail, link);
                    }
                }
            });

            links.remove(missingLinks);

            this.addPseudoLinkIfEmpty(links);
        });
        this.renderPublisherView(publishers);
    }

    private renderPublisherView(publishers: Backbone.Collection<PublisherModel>) {
        if (this.byPublisherView) {
            this.byPublisherView.collection = publishers;
            this.byPublisherView.render();
            this.$(".publisher-full-text-link-loading").hide();
        }
    }

    private addPseudoLinkIfEmpty(links: Backbone.Collection<CitationLinkModel>) {
        if (links.length === 0) {
            const psuedoLink = new CitationLinkModel();
            psuedoLink.NoLinks = true;
            psuedoLink.DisplayLink = true;
            links.push(psuedoLink);
        }
    }

    private determineLinkTypes(code: string, reportSources: string[], link: CitationLinkModel, fullTextSources: string[]) {
        const source = code.toLowerCase();
        const mappedSource = this.getMappedIndicator(source);
        const reportSourceFound = reportSources && reportSources.length > 0 && reportSources.indexOf(link.Source) > -1;
        const judgmentSourceFound = fullTextSources && fullTextSources.length > 0 && fullTextSources.indexOf(mappedSource) > -1;
        link.IsJudgmentUrl = judgmentSourceFound;
        link.IsReportUrl = reportSourceFound;
        link.IsLegislationUrl = source === "sta" || source === "iracts" || source === "si";
    }

    private addToExistingPublisher(existingPublisher: any, citation: string, detail: ParallelCitationModel, link: CitationLinkModel) {
        existingPublisher.get("Citations").push({
            Citation: citation,
            Name: detail.get("Source").Name,
            Url: link.get("Url"),
            SiteId: link.get("SiteId"),
            IsJustisOneUrl: link.get("IsJustisOneUrl"),
            IsJudgmentUrl: link.get("IsJudgmentUrl"),
            IsLegislationUrl: link.get("IsLegislationUrl"),
            Source: link.get("Source")
        });
    }

    private renderLinksByCitation(data: Backbone.Collection<CitationLinksModel>) {
        data.forEach((linkList) => {
            // get the right list for the particular citation
            const citation = linkList.Citation;
            const $parent = this.$("li[data-reference=\"" + citation + "\"]");
            const $headerCitation = $parent.find(".full-text-source-heading .citation");
            const container = $parent.find(".full-text-links-list").get(0);

            // use container as el for views
            const newView = new FullTextLinksRelationsView({ collection: linkList.Links, el: container });
            newView.render();

            // if this linkList has a lookupUrl, put that in the header
            if (linkList.HasLookup) {
                const lookupLink = linkList.CitationLookupLink;
                const renderedHtml = Mustache.to_html(lookupLinkTemplate, { Model: lookupLink });
                $headerCitation.append($(renderedHtml));
            }
        });

        this.sourceCollection.fetchSettings().then(() => {
            // show/hide the "show all" button
            if (this.authService.AppUser.AllowAdmin || this.sourceCollection.CanUsersChangeSettings) {
                if (this.model.Data.Publishers.findWhere({DisplayLink: false})) {
                    this.$(".btn-toggle-sources").show();
                } else {
                    this.$(".btn-toggle-sources").hide();
                }
            } else {
                this.$(".btn-toggle-sources").hide();
            }
        });
    }

    private fullTextSourcesToggleClick(): void {
        this.model.State.ShowAllSources = !this.model.State.ShowAllSources;

        if (this.model.State.ShowAllSources) {
            this.$(".full-text-sources-list").addClass("show-all");
            this.$(".deactivated").addClass("border-glow"); // Glow for 2 seconds, then remove it
            setTimeout(() => { this.$(".deactivated").removeClass("border-glow"); }, 2 * 1000);
        } else {
            this.$(".full-text-sources-list").removeClass("show-all");
        }
    }

    private getMappedIndicator(source: string): string {
        let mappedIndicator;
        if (this.model.Data.FullTextDetails) {
            this.model.Data.FullTextDetails.forEach((element) => {
                if (element.Name.includes("Core")) {
                    return;
                }

                const foundReport = element.Reports.find((rep) => rep.Code === source);
                if (foundReport) {
                    mappedIndicator = element.Indicator;
                }
            });
        }
        return mappedIndicator ? mappedIndicator : source;
    }

    private getJustisOneFullTextSources(): string[] {
        const fullTextCodes = [];
        if (this.model.Data.FullTextDetails) {
            this.model.Data.FullTextDetails.forEach((ft) => {
                if (ft.Name.includes("Core")) {
                    ft.Reports.forEach((rep) => {
                        fullTextCodes.push(rep.Code);
                    });
                }
            });
        }
        return fullTextCodes;
    }

    private getJustisOneReportSources(): string[] {
        const reportRefs = [];
        if (this.model.Data.ReportDetails) {
            this.model.Data.ReportDetails.forEach((rep) => {
                reportRefs.push(rep.Source);
            });
        }
        return reportRefs;
    }

    private linkSortClick(e: JQueryEventObject): void {
        const linkSortSelector = ".js-link-sort";
        const target = this.$(e.currentTarget);
        if (target.hasClass("disabled")) {
            return;
        }
        this.$(linkSortSelector).removeClass("disabled").attr("aria-checked", "false");
        target.addClass("disabled").attr("aria-checked", "true");

        const mode = target.attr("data-mode");
        switch (mode) {
            case "Publisher":
                this.$(".sources-by-citation-region").hide();
                this.$(".sources-by-publisher-region").show();
                break;
            case "Citation":
                this.$(".sources-by-citation-region").show();
                this.$(".sources-by-publisher-region").hide();
                break;
        }
    }

    private fullTextLinkClick(e: JQueryEventObject, linkType: string): void {
        e.preventDefault();
        e.stopPropagation();
        const currentLink = $(e.currentTarget).find("a");
        const isJustisOneUrl = currentLink.attr("data-justis-one-url") === "true";
        const isJudgmentUrl = currentLink.attr("data-is-judgment-url") === "true";
        const isLegislationUrl = currentLink.attr("data-is-legislation-url") === "true";
        if (isJustisOneUrl) {
            const source = currentLink.attr("data-source");
            const newUrl = this.createNewJustisOneUrl(isJudgmentUrl, isLegislationUrl, source);
            Backbone.history.navigate(newUrl, {trigger: true});
        } else {
            const url = currentLink.attr("href");
            const win = window.open(url);
            win.focus();
        }
    }

    private createNewJustisOneUrl(isJudgmentUrl: boolean, isLegislationUrl: boolean, source: string): string {
        let newUrl: string;
        if (isJudgmentUrl) {
            newUrl = this.model.Data.UrlTemplate.replace("{section}", "fulltext-judgment");
        } else if (isLegislationUrl) {
            newUrl = this.model.Data.UrlTemplate.replace("{section}", "fulltext-legislation");
        } else {
            if (this.model.Data.ReportDetails && this.model.Data.ReportDetails.length > 0) {
                const foundTab = this.model.Data.FulltextTabs.findWhere({ Source: source });
                newUrl = this.model.Data.UrlTemplate.replace("{section}", foundTab.Identifier);
            } else {
                newUrl = this.model.Data.UrlTemplate.replace("{section}", "eu-fulltext");
            }
        }

        return newUrl;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/views/tabs/citations-and-sources.ts